import { useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { v4 } from "uuid";

import { VariableSet } from "../../../../Playground/types";

import { getImportColumns, getRowVariables } from "./table-rows-import-utils";

import { ChooseEntityDialogControlled } from "@/components/ChooseEntityDialog";
import LoadingSpinner from "@/components/LoadingSpinner";
import { Button } from "@/components/ui/button";
import Checkbox from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/auth-context";
import { ToastType } from "@/enums";
import { Entity, EntityType } from "@/queries";
import { useEvaluateSheetGrid } from "@/queries/playground/evaluate";
import { displayErrorToast, displayToast } from "@/utils/toast";

const MAX_VARIABLE_SETS = 5;
const MAX_ROWS_SHOWN = 200;

interface SelectedSheet {
  tableId: string;
  sheetId: string;
  name: string;
}

interface TableRowsImportProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onVariablesFetched?: (variables: Record<string, string>, setName?: string) => void;
  onAddVariableSet?: (variableSet: VariableSet) => void;
  currentVariableSetCount?: number;
  variableNames?: string[];
}

export const TableRowsImport = ({
  isOpen,
  onOpenChange,
  onVariablesFetched,
  onAddVariableSet,
  currentVariableSetCount = 0,
  variableNames = [],
}: TableRowsImportProps) => {
  const userToken = useAuth()?.userToken ?? "";
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const [sheet, setSheet] = useState<SelectedSheet | null>(null);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  // The picker closes itself right after a selection, before `sheet` state updates.
  const hasPickedSheetRef = useRef(false);

  // The first row replaces the set being edited, so one more row than free slots fits.
  const maxSelectable = MAX_VARIABLE_SETS - currentVariableSetCount + 1;

  const { data: grid, isLoading } = useEvaluateSheetGrid({
    userToken,
    workspaceId: Number(workspaceId),
    smartTableId: sheet?.tableId ?? "",
    sheetId: sheet?.sheetId ?? "",
    limit: MAX_ROWS_SHOWN,
  });

  const importColumns = useMemo(
    () => getImportColumns(grid?.columns ?? [], variableNames),
    [grid?.columns, variableNames],
  );
  const rows = grid?.rows ?? [];

  const close = () => {
    hasPickedSheetRef.current = false;
    setSheet(null);
    setSelectedRows([]);
    onOpenChange(false);
  };

  const handleSelectSheet = (entity: Entity<EntityType.SMART_TABLE>) => {
    const sheetId = (entity as Entity<EntityType.SMART_TABLE> & { selected_sheet_id?: string })
      .selected_sheet_id;
    if (!sheetId) {
      displayErrorToast("Choose a sheet.");
      return;
    }
    hasPickedSheetRef.current = true;
    setSheet({ tableId: String(entity.id), sheetId, name: entity.name });
  };

  const toggleRow = (rowIndex: number) =>
    setSelectedRows(current => {
      if (current.includes(rowIndex)) {
        return current.filter(index => index !== rowIndex);
      }
      return current.length >= maxSelectable ? current : [...current, rowIndex];
    });

  const handleImport = () => {
    if (!sheet) {
      return;
    }
    const chosen = rows.filter(row => selectedRows.includes(row.row_index));
    chosen.forEach((row, index) => {
      const variables = getRowVariables(row, importColumns);
      const setName = `${sheet.name} (row ${row.row_index + 1})`;
      if (index === 0) {
        onVariablesFetched?.(variables, setName);
        return;
      }
      onAddVariableSet?.({
        id: v4(),
        name: setName,
        variables: Object.entries(variables).map(([key, value]) => ({ id: v4(), key, value })),
      });
    });
    displayToast(
      chosen.length === 1 ? "Imported 1 row" : `Imported ${chosen.length} rows`,
      ToastType.success,
    );
    close();
  };

  const previewColumns = importColumns.slice(0, 3);

  return (
    <>
      <ChooseEntityDialogControlled
        open={isOpen && !sheet}
        onOpenChange={open => {
          if (!open && !hasPickedSheetRef.current) {
            close();
          }
        }}
        type={EntityType.SMART_TABLE}
        currentFolderId={null}
        mode="single"
        onSelect={handleSelectSheet}
        config={{ selectSmartTableSheet: true, selectLatest: true }}
      />
      <Dialog open={isOpen && Boolean(sheet)} onOpenChange={open => !open && close()}>
        <DialogContent className="sm:max-w-[640px]">
          <DialogHeader>
            <DialogTitle>Import from {sheet?.name ?? "table"}</DialogTitle>
            <DialogDescription>
              Each row becomes an input set. Select up to {maxSelectable}.
            </DialogDescription>
          </DialogHeader>
          {isLoading ? (
            <div className="flex justify-center py-10">
              <LoadingSpinner size={6} />
            </div>
          ) : (
            <div className="max-h-[420px] divide-y divide-gray-100 overflow-y-auto rounded-md border border-gray-200">
              {rows.length === 0 ? (
                <div className="px-3 py-6 text-center text-sm text-gray-500">No rows</div>
              ) : (
                rows.map(row => {
                  const isSelected = selectedRows.includes(row.row_index);
                  const values = getRowVariables(row, previewColumns);
                  return (
                    <label
                      key={row.row_index}
                      className="flex cursor-pointer items-start gap-3 px-3 py-2 hover:bg-gray-50"
                    >
                      <Checkbox
                        checked={isSelected}
                        disabled={!isSelected && selectedRows.length >= maxSelectable}
                        onChange={() => toggleRow(row.row_index)}
                        className="mt-0.5"
                      />
                      <span className="w-12 shrink-0 text-xs text-gray-400">
                        Row {row.row_index + 1}
                      </span>
                      <span className="min-w-0 flex-1 space-y-0.5">
                        {previewColumns.map(column => (
                          <span key={column.id} className="block truncate text-xs text-gray-700">
                            <span className="text-gray-400">{column.title}:</span>{" "}
                            {values[column.title] || "(empty)"}
                          </span>
                        ))}
                      </span>
                    </label>
                  );
                })
              )}
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                hasPickedSheetRef.current = false;
                setSheet(null);
              }}
            >
              Back
            </Button>
            <Button onClick={handleImport} disabled={selectedRows.length === 0}>
              {selectedRows.length > 1 ? `Import ${selectedRows.length} rows` : "Import row"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
